import posthog from "posthog-js";

console.log("posthog");

class PostHogService {
  constructor() {
    this.initialized = false;
  }

  initialize() {
    if (this.initialized || typeof window === "undefined") {
      return;
    }

    const apiKey = process.env.REACT_APP_POSTHOG_KEY;
    const apiHost =
      process.env.REACT_APP_POSTHOG_HOST || "https://app.posthog.com";

    if (!apiKey) {
      console.warn("PostHog API key not found. Analytics will not be tracked.");
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: apiHost,
        loaded: (posthog) => {
          if (process.env.NODE_ENV === "development") {
            console.log("PostHog initialized (development mode)");
          }
        },
        autocapture: true, // Automatically capture clicks, form submissions, etc.
        capture_pageview: true, // Automatically capture page views
        capture_pageleave: true, // Capture when users leave pages
        session_recording: {
          // Enable session recordings for better user journey analysis
          maskAllInputs: true, // Mask sensitive input fields
          maskTextSelector: "[data-ph-mask]", // Mask elements with this attribute
        },
      });

      this.initialized = true;
      console.log("PostHog initialized successfully");
    } catch (error) {
      console.error("Failed to initialize PostHog:", error);
    }
  }

  // Identify user with unique ID and properties
  identify(userId, userProperties = {}) {
    if (!this.initialized) {
      console.warn("PostHog not initialized yet, cannot identify user");
      return;
    }

    if (!userId) {
      console.warn("No userId provided for identification");
      return;
    }

    try {
      console.log("Identifying user in PostHog:", userId, userProperties);
      posthog.identify(userId, {
        email: userProperties.email,
        name: userProperties.name,
        subscription_type: userProperties.subscriptionType,
        created_at: userProperties.createdAt,
        ...userProperties,
      });
      console.log("User identified successfully in PostHog");
    } catch (error) {
      console.error("PostHog identify error:", error);
    }
  }

  // Track custom events
  track(eventName, properties = {}) {
    if (!this.initialized) return;

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error("PostHog track error:", error);
    }
  }

  // Track page views manually
  trackPageView(path) {
    if (!this.initialized) return;

    try {
      posthog.capture("$pageview", {
        $current_url: window.location.href,
        path: path || window.location.pathname,
      });
    } catch (error) {
      console.error("PostHog pageview error:", error);
    }
  }

  // Reset user session (call on logout)
  reset() {
    if (!this.initialized) return;

    try {
      posthog.reset();
    } catch (error) {
      console.error("PostHog reset error:", error);
    }
  }

  // Set user properties
  setUserProperties(properties) {
    if (!this.initialized) return;

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error("PostHog set properties error:", error);
    }
  }

  // Feature flags
  isFeatureEnabled(flagKey) {
    if (!this.initialized) return false;

    try {
      return posthog.isFeatureEnabled(flagKey);
    } catch (error) {
      console.error("PostHog feature flag error:", error);
      return false;
    }
  }

  // Get feature flag value
  getFeatureFlag(flagKey) {
    if (!this.initialized) return null;

    try {
      return posthog.getFeatureFlag(flagKey);
    } catch (error) {
      console.error("PostHog get feature flag error:", error);
      return null;
    }
  }
}

// Export singleton instance
const posthogService = new PostHogService();
export default posthogService;
